import { useEffect, useRef, useState } from 'react';

interface UseTypewriterTextOptions {
  active?: boolean;
  interval?: number;
  minStep?: number;
  maxStep?: number;
  onTyping?: () => void;
  onComplete?: (text: string) => void;
  forceComplete?: boolean;
}

const getStep = (remaining: number, minStep: number, maxStep: number) => {
  if (remaining > 200) return maxStep;
  if (remaining > 80) return Math.max(minStep, Math.ceil(maxStep / 2));
  return minStep;
};

export const useTypewriterText = (
  targetText: string,
  {
    active = false,
    interval = 16,
    minStep = 1,
    maxStep = 6,
    onTyping,
    onComplete,
    forceComplete = false,
  }: UseTypewriterTextOptions = {},
) => {
  const target = targetText ?? '';
  const [displayText, setDisplayText] = useState(() => (active ? '' : target));
  const displayTextRef = useRef(displayText);
  const hasAnimatedRef = useRef(active);
  const completedTargetRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (active) {
      hasAnimatedRef.current = true;
    }

    const commitText = (nextText: string, shouldNotify = true) => {
      const hasChanged = displayTextRef.current !== nextText;
      displayTextRef.current = nextText;
      setDisplayText(nextText);
      if (hasChanged && shouldNotify) {
        onTyping?.();
      }
    };
    const notifyComplete = () => {
      if (displayTextRef.current !== target || completedTargetRef.current === target) return;
      completedTargetRef.current = target;
      onComplete?.(target);
    };

    if (forceComplete) {
      commitText(target, false);
      notifyComplete();
      return undefined;
    }

    const currentText = displayTextRef.current;
    const canContinueTyping =
      target.startsWith(currentText) && (active || (hasAnimatedRef.current && currentText !== target));

    if (!canContinueTyping) {
      commitText(target);
      notifyComplete();
      return undefined;
    }

    const tick = () => {
      const latestText = displayTextRef.current;
      if (!target.startsWith(latestText)) {
        commitText(target);
        notifyComplete();
        return;
      }

      const remaining = target.length - latestText.length;
      if (remaining <= 0) {
        notifyComplete();
        return;
      }

      const step = getStep(remaining, minStep, maxStep);
      const nextText = target.slice(0, latestText.length + step);
      commitText(nextText);

      if (nextText !== target) {
        timerRef.current = setTimeout(tick, interval);
      } else {
        notifyComplete();
      }
    };

    timerRef.current = setTimeout(tick, interval);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, forceComplete, interval, maxStep, minStep, onComplete, onTyping, target]);

  return displayText;
};
